import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuthState, useSignInWithGoogle, useSignInWithEmailAndPassword } from "react-firebase-hooks/auth";
import auth from "../firebase.init";
import Loading from "../components/Shared/Loading";

const getErrorMessage = (errorCode) => {
  switch (errorCode) {
    case "auth/popup-closed-by-user":
      return "Sign-in popup closed by user.";
    case "auth/popup-blocked":
      return "Sign-in popup was blocked. Please allow popups for this site.";
    case "auth/cancelled-popup-request":
      return "Sign-in cancelled. Please try again.";
    case "auth/network-request-failed":
      return "Network error. Please check your connection and try again.";
    default:
      return "An error occurred during registration. Please try again.";
  }
};

const Login = () => {
  const [signInWithGoogle, gUser, gLoading, gError] = useSignInWithGoogle(auth);
  const [signInWithEmailAndPassword, eUser, eLoading, eError] = useSignInWithEmailAndPassword(auth);
  const [apiError, setApiError] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  const [userMail] = useAuthState(auth);

  useEffect(() => {
    if (userMail) {
      navigate("/", { replace: true });
    }
  }, [userMail, navigate]);

  let from = location.state?.from?.pathname || "/";
  let signUpError;

  useEffect(() => {
    if (gError || apiError) {
      window.scrollTo(0, 0);
    }
  }, [gError, apiError]);

  // Create user in database after successful Firebase authentication
  const createUserInDb = async (name, email, photoURL) => {
    try {
      const response = await fetch(`https://emailserverjuly.shahlimon.online/api/create-user`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          userName: name,
          userEmail: email,
          profileURL: photoURL || null,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        if (data.error === "User already exists") {
          return data;
        }
        throw new Error(data.error || "Failed to create user");
      }

      console.log("User created with credits:", data);
      return data;
    } catch (err) {
      console.error("Error creating user in database:", err);
      setApiError(err.message);
      return null;
    }
  };

  useEffect(() => {
    const handleUserCreation = async () => {
      if (gUser || eUser) {
        setIsProcessing(true);
        const user = auth.currentUser;
        if (user) {
          const { displayName, email, photoURL } = user;
          await createUserInDb(displayName || "Demo User", email, photoURL);
        }
        setIsProcessing(false);
        navigate(from, { replace: true });
      }
    };

    handleUserCreation();
  }, [gUser, eUser, from, navigate]);

  if (gLoading || eLoading || isProcessing) {
    return <Loading />;
  }

  if (gError || eError || apiError) {
    const errorCode = gError?.code || eError?.code;
    signUpError = (
      <div className="error-message">
        <i className="fas fa-exclamation-circle"></i>
        <p>{apiError || getErrorMessage(errorCode)}</p>
      </div>
    );
  }

  const handleGoogleSignIn = () => {
    setApiError(null);
    signInWithGoogle();
  };

  const handleDemoLogin = () => {
    setApiError(null);
    signInWithEmailAndPassword("demo@admin.com", "123456");
  };

  return (
    <>
      <Helmet>
        <title>Login — Update Email Marketing Tools</title>
        <meta
          name="description"
          content="Sign in to Update Email Marketing Tools — AI-powered email tracking platform with real-time analytics, open tracking, and click tracking."
        />
        <link rel="canonical" href="https://updateemailmarketingtoolsdemo.web.app/login" />
      </Helmet>

      <section className="modern-register-page">
        <div className="register-container">
          <div className="register-card">
            <div className="register-header">
              <div className="close-button">
                <Link to="/" className="close-link">
                  <i className="fas fa-times"></i>
                </Link>
              </div>

              <div className="logo-section">
                <Link to="/">
                  
                </Link>
              </div>
              <div className="welcome-section">
                <h1>Welcome Back</h1>
                <p>Sign in to continue to your account</p>
              </div>
            </div>

            <div className="register-content">
              {signUpError}

              <div className="demo-login-section">
                <div className="demo-info">
                  <p>Want to test it out?</p>
                  <div className="demo-credentials">
                    <span>Email: <strong>demo@admin.com</strong></span>
                    <span>Pass: <strong>123456</strong></span>
                  </div>
                </div>
                <button
                  onClick={handleDemoLogin}
                  className="demo-signin-btn"
                  disabled={gLoading || eLoading || isProcessing}
                >
                  <i className="fas fa-user-shield"></i>
                  <span>Login as Demo User</span>
                </button>
              </div>


              <div className="features-section">
                <h3>Your Account Benefits?</h3>
                <div className="features-grid">
                  <div className="feature-item">
                    <i className="fas fa-check-circle"></i>
                    <span>Personalized dashboard</span>
                  </div>
                  <div className="feature-item">
                    <i className="fas fa-check-circle"></i>
                    <span>Save your Preferences</span>
                  </div>
                  <div className="feature-item">
                    <i className="fas fa-check-circle"></i>
                    <span>Track your progress</span>
                  </div>
                  <div className="feature-item">
                    <i className="fas fa-check-circle"></i>
                    <span>24/7 Support</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="register-footer">
             

              <div className="terms-section">
                {/* <p>By continuing, you agree to our <a href="/terms">Terms of Service</a> and <a href="/privacy">Privacy Policy</a></p> */}
              </div>
            </div>
          </div>
        </div>

        <style jsx>{`
          .demo-login-section {
            margin-bottom: 25px;
            background: rgba(66, 133, 244, 0.05);
            padding: 20px;
            border-radius: 15px;
            border: 1px dashed #4285f4;
          }

          .demo-info {
            text-align: center;
            margin-bottom: 15px;
          }

          .demo-info p {
            font-size: 14px;
            color: #666;
            margin-bottom: 8px;
          }

          .demo-credentials {
            display: flex;
            justify-content: center;
            gap: 15px;
            font-size: 13px;
            color: #444;
          }

          .demo-credentials strong {
            color: #4285f4;
          }

          .demo-signin-btn {
            width: 100%;
            height: 50px;
            background: #4285f4;
            border: none;
            border-radius: 12px;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 10px;
            font-size: 16px;
            font-weight: 600;
            color: #fff;
            cursor: pointer;
            transition: all 0.3s ease;
          }

          .demo-signin-btn:hover {
            background: #3367d6;
            box-shadow: 0 4px 12px rgba(66, 133, 244, 0.3);
            transform: translateY(-2px);
          }

          .divider {
            display: flex;
            align-items: center;
            text-align: center;
            margin-bottom: 25px;
            color: #999;
            font-size: 12px;
            font-weight: 600;
          }

          .divider::before,
          .divider::after {
            content: "";
            flex: 1;
            border-bottom: 1px solid #eee;
          }

          .divider span {
            padding: 0 10px;
          }

          .modern-register-page {
            min-height: 100vh;
            background-image: url(/images/shapes/login-page-shape-1.png);
            background-size: cover;
            background-position: center;
            background-repeat: no-repeat;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 20px;
            position: relative;
            overflow: hidden;
          }

          .modern-register-page::before {
            content: "";
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, 0.3);
            backdrop-filter: blur(1px);
          }

          .register-container {
            position: relative;
            z-index: 1;
            width: 100%;
            max-width: 500px;
          }

          .register-card {
            background: rgba(255, 255, 255, 0.95);
            backdrop-filter: blur(10px);
            border-radius: 20px;
            box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
            overflow: hidden;
            border: 1px solid rgba(255, 255, 255, 0.2);
          }

          .register-header {
            padding: 40px 40px 30px;
            text-align: center;
            position: relative;
          }

          .close-button {
            position: absolute;
            top: 20px;
            right: 20px;
          }

          .close-link {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 40px;
            height: 40px;
            background: rgba(0, 0, 0, 0.1);
            border-radius: 50%;
            color: #666;
            text-decoration: none;
            transition: all 0.3s ease;
          }

          .close-link:hover {
            background: rgba(0, 0, 0, 0.2);
            color: #333;
          }

          .logo-section {
            margin-bottom: 30px;
          }

          .logo {
            height: 60px;
            width: auto;
          }

          .welcome-section h1 {
            font-size: 28px;
            font-weight: 700;
            color: #333;
            margin-bottom: 10px;
          }

          .welcome-section p {
            font-size: 16px;
            color: #666;
            margin: 0;
          }

          .register-content {
            padding: 0 40px 30px;
          }

          .error-message {
            background: #fee;
            border: 1px solid #fcc;
            border-radius: 12px;
            padding: 15px;
            margin-bottom: 25px;
            display: flex;
            align-items: center;
            gap: 10px;
          }

          .error-message i {
            color: #d32f2f;
            font-size: 18px;
          }

          .error-message p {
            color: #d32f2f;
            margin: 0;
            font-size: 14px;
          }

          .google-signin-section {
            margin-bottom: 30px;
          }

          .google-signin-btn {
            width: 100%;
            height: 56px;
            background: #fff;
            border: 2px solid #e0e0e0;
            border-radius: 12px;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 12px;
            font-size: 16px;
            font-weight: 500;
            color: #333;
            cursor: pointer;
            transition: all 0.3s ease;
            position: relative;
            overflow: hidden;
          }

          .google-signin-btn:hover {
            border-color: #4285f4;
            box-shadow: 0 4px 12px rgba(66, 133, 244, 0.2);
            transform: translateY(-2px);
          }

          .google-signin-btn:active {
            transform: translateY(0);
          }

          .google-signin-btn:disabled {
            opacity: 0.7;
            cursor: not-allowed;
            transform: none;
          }

          .google-icon {
            display: flex;
            align-items: center;
            justify-content: center;
          }

          .features-section {
            text-align: center;
          }

          .features-section h3 {
            font-size: 20px;
            font-weight: 600;
            color: #333;
            margin-bottom: 20px;
          }

          .features-grid {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 15px;
          }

          .feature-item {
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 12px;
            background: rgba(102, 126, 234, 0.1);
            border-radius: 8px;
            font-size: 14px;
            color: #555;
          }

          .feature-item i {
            color: #667eea;
            font-size: 16px;
          }

          .register-footer {
            padding: 30px 40px;
            background: rgba(0, 0, 0, 0.02);
            text-align: center;
            border-top: 1px solid rgba(0, 0, 0, 0.05);
          }

          .register-footer p {
            margin: 0 0 15px;
            color: #666;
            font-size: 14px;
          }

          .signin-link {
            color: #667eea;
            text-decoration: none;
            font-weight: 500;
          }

          .signin-link:hover {
            text-decoration: underline;
          }

          .terms-section p {
            font-size: 12px;
            color: #999;
            margin: 0;
          }

          .terms-section a {
            color: #667eea;
            text-decoration: none;
          }

          .terms-section a:hover {
            text-decoration: underline;
          }

          @media (max-width: 768px) {
            .modern-register-page {
              padding: 10px;
            }

            .register-header {
              padding: 30px 30px 20px;
            }

            .register-content {
              padding: 0 30px 20px;
            }

            .register-footer {
              padding: 20px 30px;
            }

            .welcome-section h1 {
              font-size: 24px;
            }

            .features-grid {
              grid-template-columns: 1fr;
            }

            .feature-item {
              justify-content: center;
            }
          }

          @media (max-width: 480px) {
            .register-header {
              padding: 20px 20px 15px;
            }

            .register-content {
              padding: 0 20px 15px;
            }

            .register-footer {
              padding: 15px 20px;
            }

            .welcome-section h1 {
              font-size: 22px;
            }

            .welcome-section p {
              font-size: 14px;
            }

            .google-signin-btn {
              height: 50px;
              font-size: 14px;
            }
          }
        `}</style>
      </section>
    </>
  );
};

export default Login;
